import React, { Component, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { seatingDetailsErrorStrings, seatingDetailsRules } from 'shared/validations';
import { ValidationService } from 'shared/services';
import { Input } from 'shared/components';
import './SeatingDetails.scss';

const seatingDetails = {
  seatingRowCount: 0,
  seatingColumnCount: 0,
  writingSurfaceWidth: 0,
  writingSurfaceDepth: 0,
  distanceBetweenShoulders: 0,
  distanceBetweenHeads: 0,
};

class SeatingDetails extends Component {
  constructor(props) {
    super(props);

    // Set up the ref if provided
    if (props.forwardedRef) {
      props.forwardedRef.current = this;
    }

    this.state = {
      room: Object.assign({}, seatingDetails, props.room),
      isDirty: false,
      errors: {},
    };

    this.onSeatingDetailsChange = this.onSeatingDetailsChange.bind(this);
  }

  componentDidUpdate(prevProps) {
    if (
      !this.state.room.id &&
      !this.props.room.loading &&
      !isEmpty(this.props.room) &&
      prevProps.room !== this.props.room
    ) {
      this.setState({
        room: this.props.room,
      });
    }
  }

  onSubmit() {
    const { room } = this.state;
    let errors = {};
    let seatsError;

    if (this.props.required) {
      errors = ValidationService.validate(room, seatingDetailsRules, seatingDetailsErrorStrings);
    }

    //check number of seats to be maximum 200
    if (room.seatingRowCount * room.seatingColumnCount > 200) {
      seatsError = 'seatingChart.seatingDetails.nrOfSeatsError';
      this.setState({
        seatsError,
      });
    }

    const newErrors = Object.assign({}, this.state.errors, errors);
    this.setState({
      errors: newErrors,
    });

    if (
      (!isEmpty(newErrors) && Object.keys(newErrors).some((key) => newErrors[key])) ||
      seatsError
    ) {
      return;
    }

    return room;
  }

  onSeatingDetailsChange(e) {
    let { value, name } = e.target;
    let { seatsError } = this.state;
    let error;

    if (+value < 0) {
      value = 0;
    }

    if (value.length > 1 && +value[0] === 0) {
      value = value.substring(1);
    }

    if (value !== '' && +value === 0) {
      error = ['errors.zeroNotAllowed'];
    }

    if (!error) {
      if (name === 'seatingColumnCount' || name === 'seatingRowCount') {
        //check max value
        error = +value > 25 ? ['seatingChart.seatingDetails.maxNrOfSeatsError'] : '';
        seatsError = null;
      } else if (name === 'distanceBetweenShoulders' || name === 'distanceBetweenHeads') {
        if (+value < 1 && value.length <= 2) {
          error = ['seatingChart.seatingDetails.minimumFeat'];
        } else if (value.length > 2) {
          error = this.props.t('errors.fieldDigitLimitation', { limit: 2 });
        } else {
          error = '';
        }
      } else {
        //check field limit 2
        error = value.length > 2 ? this.props.t('errors.fieldDigitLimitation', { limit: 2 }) : '';
      }
    }

    this.setState((prevState) => ({
      room: {
        ...prevState.room,
        [name]: value,
      },
      errors: {
        ...prevState.errors,
        [name]: error || null,
      },
      seatsError,
      isDirty: true,
    }));
  }

  render() {
    const { room, errors, seatsError } = this.state;

    return (
      <div className="seating-details-container">
        <div className="seating-input-group">
          <label
            className={`${
              errors.seatingRowCount || errors.seatingColumnCount || seatsError ? 'label-error' : ''
            } fw-bold w-100`}>
            {this.props.t('seatingChart.seatingDetails.nrOfSeatsLabel')}
          </label>
          <div className="input-section">
            <Input
              type="number"
              containerClassName={seatsError && !errors.seatingRowCount ? 'hide-error' : ''}
              name="seatingRowCount"
              id="seatingRowCount"
              value={room.seatingRowCount || ''}
              onChange={this.onSeatingDetailsChange}
              errors={errors.seatingRowCount || seatsError}
              required
            />
            <span>{this.props.t('seatingChart.seatingDetails.rowsOf')}</span>
            <Input
              type="number"
              containerClassName={seatsError && !errors.seatingColumnCount ? 'hide-error' : ''}
              name="seatingColumnCount"
              id="seatingColumnCount"
              value={room.seatingColumnCount || ''}
              onChange={this.onSeatingDetailsChange}
              errors={errors.seatingColumnCount || seatsError}
              required
            />
            <span>{this.props.t('common.labels.seats')}</span>
          </div>
          {seatsError && (
            <div className="text-error name-error font-size-12">{this.props.t(seatsError)}</div>
          )}
        </div>
        <div className="seating-input-group">
          <label
            className={`${
              errors.writingSurfaceWidth || errors.writingSurfaceDepth ? 'label-error' : ''
            } fw-bold w-100`}>
            {this.props.t('seatingChart.seatingDetails.writingSurfacesLabel')}
          </label>
          <div className="input-section">
            <Input
              type="number"
              name="writingSurfaceWidth"
              id="writingSurfaceWidth"
              value={room.writingSurfaceWidth || ''}
              onChange={this.onSeatingDetailsChange}
              errors={errors.writingSurfaceWidth}
              required
            />
            <span>{this.props.t('seatingChart.seatingDetails.inchBy')}</span>
            <Input
              type="number"
              name="writingSurfaceDepth"
              id="writingSurfaceDepth"
              value={room.writingSurfaceDepth || ''}
              onChange={this.onSeatingDetailsChange}
              errors={errors.writingSurfaceDepth}
              required
            />
            <span>{this.props.t('common.labels.inch')}</span>
          </div>
        </div>
        <div className="seating-input-group">
          <label
            className={`${errors.distanceBetweenShoulders ? 'label-error' : ''} fw-bold w-100`}>
            {this.props.t('seatingChart.seatingDetails.shoulderDistanceLabel')}
          </label>
          <div className="input-section">
            <Input
              type="number"
              name="distanceBetweenShoulders"
              id="distanceBetweenShoulders"
              value={room.distanceBetweenShoulders || ''}
              onChange={this.onSeatingDetailsChange}
              errors={errors.distanceBetweenShoulders}
              required
            />
            <span>{this.props.t('common.labels.feet')}</span>
          </div>
          {!errors.distanceBetweenShoulders && (
            <p className="font-size-12 mt-1">
              {this.props.t('seatingChart.seatingDetails.minimumFeat')}{' '}
            </p>
          )}
        </div>
        <div className="seating-input-group">
          <label className={`${errors.distanceBetweenHeads ? 'label-error' : ''} fw-bold w-100`}>
            {this.props.t('seatingChart.seatingDetails.headDistanceLabel')}
          </label>
          <div className="input-section">
            <Input
              type="number"
              name="distanceBetweenHeads"
              id="distanceBetweenHeads"
              value={room.distanceBetweenHeads || ''}
              onChange={this.onSeatingDetailsChange}
              errors={errors.distanceBetweenHeads}
              required
            />
            <span>{this.props.t('common.labels.feet')}</span>
          </div>
          {!errors.distanceBetweenHeads && (
            <p className="font-size-12 mt-1">
              {this.props.t('seatingChart.seatingDetails.minimumFeat')}{' '}
            </p>
          )}
        </div>
      </div>
    );
  }
}

SeatingDetails.propTypes = {
  room: PropTypes.shape({}).isRequired,
  required: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

SeatingDetails.defaultProps = {
  required: false,
};

const TranslatedSeatingDetails = withTranslation()(SeatingDetails);

// Forward ref to the wrapped component
const SeatingDetailsWithRef = forwardRef((props, ref) => (
  <TranslatedSeatingDetails {...props} forwardedRef={ref} />
));

SeatingDetailsWithRef.displayName = 'SeatingDetails';

export default SeatingDetailsWithRef;
